import { Alert, Autocomplete, Box, TextField, Typography } from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import type { PublicIntakeDraft } from '../../../../types/intake';
import type { Client, Project } from '../../../../types/project';
import type { ProjectInfoField } from '../intakeFormState';
import { DuplicateWarning } from '../../../../components/forms/DuplicateWarning';

/** Existing-record directory, present only for signed-in BGC users. */
export interface IntakeDirectory {
  clients: Client[];
  projects: Project[];
}

interface Props {
  draft: PublicIntakeDraft;
  onChange: (patch: Partial<PublicIntakeDraft>) => void;
  /** The lab's instructions blurb, shown once at the top of the form. */
  instructionsBlurb?: string;
  /** Fields that failed validation — highlighted red and flashed. */
  errorFields?: ProjectInfoField[];
  /** Bump to re-trigger the flash animation on the same fields. */
  flashNonce?: number;
  /**
   * Signed-in BGC users get pickers over EXISTING clients/projects (attach,
   * don't retype) with similar-name warnings for new names — the same checks
   * the app's own create dialogs use. Anonymous links keep plain text fields.
   */
  directory?: IntakeDirectory | null;
}

/** Red background pulse drawing the eye to a missing field. */
export const fieldFlashSx = {
  '@keyframes intakeFieldFlash': {
    '0%': { backgroundColor: 'rgba(198, 40, 40, 0.22)' },
    '50%': { backgroundColor: 'rgba(198, 40, 40, 0.10)' },
    '100%': { backgroundColor: 'transparent' },
  },
  animation: 'intakeFieldFlash 0.9s ease 2',
  borderRadius: 1,
} as const;

/**
 * Step 1 — the workbook's Project Information Table, minus the KeyLAB-only
 * fields (Schedule ID, Scheduled Date, Turnaround Time). Helper texts carry
 * the Instructions sheet's guidance.
 */
export const ProjectInfoStep = ({
  draft, onChange, instructionsBlurb, errorFields = [], flashNonce = 0, directory = null,
}: Props) => {
  const field = (
    key: keyof PublicIntakeDraft,
    label: string,
    helper: string,
    opts?: { required?: boolean; type?: string },
  ) => {
    const hasError = errorFields.includes(key as ProjectInfoField);
    return (
      <TextField
        key={hasError ? `${String(key)}-${flashNonce}` : String(key)}
        label={label}
        value={(draft[key] as string | null) ?? ''}
        onChange={(e) => onChange({ [key]: e.target.value || null })}
        helperText={helper}
        required={opts?.required}
        type={opts?.type}
        error={hasError}
        fullWidth
        size="small"
        InputLabelProps={opts?.type === 'date' ? { shrink: true } : undefined}
        inputProps={{ autoComplete: 'off' }}
        sx={hasError ? fieldFlashSx : undefined}
      />
    );
  };

  const selectedClient = directory?.clients.find(
    (c) => c.ClientKey === draft.clientKey,
  ) ?? null;
  const selectedProject = directory?.projects.find(
    (p) => p.ProjectKey === draft.projectKey,
  ) ?? null;

  const clientField = !directory ? field(
    'clientName', 'Client Name', 'Full client name, consistent with reports.', { required: true },
  ) : (
    <Box key="clientName">
      <Autocomplete
        freeSolo
        options={directory.clients}
        getOptionLabel={(c) => (typeof c === 'string' ? c : c.ClientName ?? '')}
        value={selectedClient ?? (draft.clientName ?? '')}
        onChange={(_, v) => {
          if (v && typeof v !== 'string') onChange({ clientName: v.ClientName ?? null, clientKey: v.ClientKey });
          else onChange({ clientName: (v as string | null) || null, clientKey: null });
        }}
        onInputChange={(_, text, reason) => {
          if (reason === 'input') onChange({ clientName: text || null, clientKey: null });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            key={errorFields.includes('clientName') ? `clientName-${flashNonce}` : 'clientName'}
            label="Client" required size="small"
            error={errorFields.includes('clientName')}
            helperText={selectedClient
              ? 'Attached to this existing client.'
              : 'Pick an existing client, or type a new name (created when the lab processes this).'}
            sx={errorFields.includes('clientName') ? fieldFlashSx : undefined}
          />
        )}
      />
      {!selectedClient && (
        <DuplicateWarning
          value={draft.clientName ?? ''}
          candidates={directory.clients}
          nameSelector={(c) => c.ClientName ?? ''}
          label="Existing clients with similar names — pick one above if it's the same client:"
        />
      )}
    </Box>
  );

  const projectField = !directory ? field(
    'projectName', 'Project Name', 'Should match the name used on reports.', { required: true },
  ) : (
    <Box key="projectName">
      <Autocomplete
        freeSolo
        options={directory.projects}
        getOptionLabel={(p) => (typeof p === 'string' ? p : `${p.ProjectId} — ${p.ProjectName}`)}
        value={selectedProject ?? (draft.projectName ?? '')}
        onChange={(_, v) => {
          if (v && typeof v !== 'string') {
            onChange({
              projectKey: v.ProjectKey,
              projectName: v.ProjectName ?? null,
              projectId: v.ProjectId ?? null,
            });
          } else {
            onChange({ projectName: (v as string | null) || null, projectKey: null });
          }
        }}
        onInputChange={(_, text, reason) => {
          if (reason === 'input') onChange({ projectName: text || null, projectKey: null });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            key={errorFields.includes('projectName') ? `projectName-${flashNonce}` : 'projectName'}
            label="Project" required size="small"
            error={errorFields.includes('projectName')}
            helperText={selectedProject
              ? undefined
              : 'Pick an existing project to add samples to it, or type a new project name.'}
            sx={errorFields.includes('projectName') ? fieldFlashSx : undefined}
          />
        )}
      />
      {selectedProject ? (
        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main', mt: 0.5 }}>
          <CheckCircleOutline sx={{ fontSize: 14 }} />
          Attached to {selectedProject.ProjectId} — samples will be added to this project's existing locations.
        </Typography>
      ) : (
        <DuplicateWarning
          value={draft.projectName ?? ''}
          candidates={directory.projects}
          nameSelector={(p) => p.ProjectName ?? ''}
          secondarySelector={(p) => p.ProjectId ?? ''}
          label="Similar projects already exist — pick one above to add to it instead:"
        />
      )}
    </Box>
  );

  return (
    <Box>
      {instructionsBlurb && <Alert severity="info" sx={{ mb: 2 }}>{instructionsBlurb}</Alert>}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This information appears on the test reports — please make sure it matches your
        other project documents.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        {projectField}
        {directory && selectedProject
          ? field('projectId', 'Project Number', 'Filled from the attached project.')
          : field('projectId', 'Project Number', 'Include the task number for billing.')}
        {field('projectLocation', 'Project Location', 'e.g. Vancouver, BC; Pueblo Viejo, Dominican Republic.')}
        {clientField}
        {field('contactName', 'Contact Person', 'Who to contact if questions arise during testing.', { required: true })}
        {field('contactEmail', 'Contact Email', 'Where to reach the contact person.', { required: true })}
        {field('dueDate', 'Due Date', 'Date you need all tests completed by (cannot be guaranteed).', { type: 'date' })}
      </Box>
      <TextField
        label="Comments for the lab"
        value={draft.clientNotes ?? ''}
        onChange={(e) => onChange({ clientNotes: e.target.value || null })}
        helperText="Anything you'd like the lab to know — sample handling, disposal plan details, special instructions."
        fullWidth
        multiline
        rows={3}
        size="small"
        sx={{ mt: 2 }}
      />
    </Box>
  );
};
